import React from 'react';
import { emptyRow } from './variantRows';
import { btnSmall, input } from './styles';

const cellInput = { ...input, padding: '6px 8px', fontSize: 13 };
const COLUMNS = [
  { key: 'color', label: 'Colour', width: 110 },
  { key: 'size', label: 'Size', width: 90 },
  { key: 'mrp', label: 'MRP ₹', width: 90, numeric: true },
  { key: 'eyantraPurchasePrice', label: 'EPP ₹', width: 90, numeric: true },
  { key: 'sku', label: 'SKU', width: 100 },
  { key: 'stockQty', label: 'Stock', width: 70, numeric: true },
];

/**
 * Rows of options (variants), each a choice on the product page — a colour, a size, or both —
 * with its own price, since price sometimes changes by size or colour. Entirely reviewer-driven:
 * nothing here is generated from the catalog's colour list.
 *
 * @param images      the draft's crops ({ id, url }) that can be shown for an option's colour
 * @param newRow      prefill for "+ Add option"
 * @param mopSet      the product has an MOP — Brandstores then charge it for every option
 */
export default function VariantsEditor({ rows, onChange, images = [], disabled, newRow = {}, mopSet, idPrefix }) {
  const update = (i, key, value) => onChange(rows.map((r, j) => (j === i ? { ...r, [key]: value } : r)));
  const remove = (i) => onChange(rows.filter((_, j) => j !== i));
  const add = () => onChange([...rows, emptyRow(newRow)]);

  const prices = new Set(rows.map((r) => r.mrp.trim()).filter(Boolean));
  const stockCount = rows.filter((r) => r.stockQty.trim() !== '').length;

  return (
    <div>
      {rows.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', minWidth: 760 }}>
            <thead>
              <tr style={{ fontSize: 11, color: '#9CA3AF', textAlign: 'left' }}>
                <th style={{ padding: '0 6px 4px 0' }}>#</th>
                {COLUMNS.slice(0, 1).map((c) => <th key={c.key} style={{ padding: '0 6px 4px 0' }}>{c.label}</th>)}
                <th style={{ padding: '0 6px 4px 0' }}>Swatch</th>
                {COLUMNS.slice(1).map((c) => <th key={c.key} style={{ padding: '0 6px 4px 0' }}>{c.label}</th>)}
                <th style={{ padding: '0 6px 4px 0' }}>Photo</th>
                <th><span className="sr-only">Remove</span></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const photo = images.find((img) => String(img.id) === r.imageId);
                return (
                  <tr key={i}>
                    <td style={{ fontSize: 12, color: '#9CA3AF', paddingRight: 6 }}>{i + 1}</td>
                    <td style={{ padding: '3px 6px 3px 0' }}>
                      <input aria-label={`Option ${i + 1} colour`} style={{ ...cellInput, width: 110 }} value={r.color}
                             disabled={disabled} onChange={(e) => update(i, 'color', e.target.value)} placeholder="e.g. Olive" />
                    </td>
                    <td style={{ padding: '3px 6px 3px 0' }}>
                      <input type="color" aria-label={`Option ${i + 1} swatch colour`} disabled={disabled}
                             value={/^#[0-9a-fA-F]{6}$/.test(r.colorHex) ? r.colorHex : '#cccccc'}
                             onChange={(e) => update(i, 'colorHex', e.target.value)}
                             style={{ width: 34, height: 30, padding: 0, border: '1.5px solid #E5E7EB', borderRadius: 6, opacity: r.colorHex ? 1 : 0.4 }}
                             title={r.colorHex ? r.colorHex : 'No swatch set — the colour name is used'} />
                    </td>
                    {COLUMNS.slice(1).map((c) => (
                      <td key={c.key} style={{ padding: '3px 6px 3px 0' }}>
                        <input aria-label={`Option ${i + 1} ${c.label}`} style={{ ...cellInput, width: c.width }} value={r[c.key]}
                               inputMode={c.numeric ? 'decimal' : undefined} disabled={disabled}
                               onChange={(e) => update(i, c.key, e.target.value)} />
                      </td>
                    ))}
                    <td style={{ padding: '3px 6px 3px 0' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <select aria-label={`Option ${i + 1} photo`} style={{ ...cellInput, width: 120 }} value={r.imageId}
                                disabled={disabled || images.length === 0} onChange={(e) => update(i, 'imageId', e.target.value)}>
                          <option value="">Main image</option>
                          {images.map((img, k) => <option key={img.id} value={String(img.id)}>Crop {k + 1}</option>)}
                        </select>
                        {photo && <img src={photo.url} alt="" style={{ width: 30, height: 30, objectFit: 'contain', borderRadius: 4, border: '1px solid #E5E7EB' }} />}
                      </div>
                    </td>
                    <td>
                      <button type="button" style={{ ...btnSmall, color: '#B91C1C', padding: '4px 8px' }} disabled={disabled}
                              onClick={() => remove(i)} aria-label={`Remove option ${i + 1}`}>×</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: rows.length ? 8 : 0 }}>
        <button type="button" style={btnSmall} disabled={disabled} onClick={add} id={idPrefix ? `${idPrefix}-add-option` : undefined}>
          + Add option
        </button>
        <span style={{ fontSize: 12, color: '#6B7280' }}>
          Each option is a choice on the product page (a colour, a size, or both). Price is per option — usually the same, sometimes more for a bigger size.
        </span>
      </div>
      {stockCount > 0 && stockCount < rows.length && (
        <div style={{ fontSize: 12, color: '#B45309', marginTop: 6 }}>Enter a stock count for every option (0 is fine), or leave them all blank.</div>
      )}
      {prices.size > 1 && mopSet && (
        <div style={{ fontSize: 12, color: '#B45309', marginTop: 6 }}>
          Options have different MRPs but an MOP is set — Brandstores charge the single MOP for every option. Clear the MOP if each option should sell at its own price.
        </div>
      )}
    </div>
  );
}
